
import { useState, useCallback } from "react";
import { Message, sendMessage } from "@/services/messages";
import { useAuth } from "@/contexts/auth";
import { useOptimisticMessages } from "./useOptimisticMessages";
import { toast } from "sonner";

const MAX_RETRIES = 3;

export function useConversation(
  messages: Message[], 
  otherUserId: string, 
  reagentId?: string,
  onMessageSent?: () => void
) {
  const { user } = useAuth();
  const [newMessage, setNewMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sendRetries, setSendRetries] = useState(0);
  
  // Use our custom hook for optimistic updates
  const { 
    optimisticMessages, 
    addOptimisticMessage,
    updateOptimisticMessage 
  } = useOptimisticMessages(messages);

  const handleSendMessage = useCallback(async () => {
    if (!newMessage.trim() || !user) return;
    
    const messageText = newMessage.trim();
    setNewMessage("");
    setIsSending(true);
    
    // Create an optimistic message
    const optimisticId = `temp-${Date.now()}`;
    const optimisticMessage: Message = {
      id: optimisticId,
      content: messageText,
      sender_id: user.id,
      recipient_id: otherUserId,
      reagent_id: reagentId,
      read: true,
      created_at: new Date().toISOString()
    };
    
    // Add optimistic message to UI
    addOptimisticMessage(optimisticMessage);
    
    try {
      const success = await sendMessage(otherUserId, messageText, reagentId);
      
      if (success) {
        // Message sent successfully
        setSendRetries(0);
        if (onMessageSent) {
          onMessageSent();
        }
      } else {
        // Message failed, but we can retry
        if (sendRetries < MAX_RETRIES) {
          toast.error(
            `Failed to send message. Retrying (${sendRetries + 1}/${MAX_RETRIES})...`, 
            { duration: 2000 }
          );
          setSendRetries(prev => prev + 1);
          
          // Update optimistic message to show retry status
          updateOptimisticMessage(optimisticId, { 
            content: `${messageText} (retrying...)` 
          });
          
          // Wait a moment and retry
          setTimeout(() => handleSendMessage(), 1000);
        } else {
          // Max retries reached
          toast.error("Could not send your message after multiple attempts");
          setSendRetries(0);
        }
      }
    } catch (err) {
      console.error("Error sending message:", err);
      toast.error("Error sending message. Please try again.");
    } finally {
      setIsSending(false);
    }
  }, [newMessage, user, otherUserId, reagentId, sendRetries, onMessageSent, addOptimisticMessage, updateOptimisticMessage]);

  return {
    newMessage,
    setNewMessage,
    isSending,
    optimisticMessages,
    handleSendMessage
  };
}
